import React from "react";
import { I18n } from "@aws-amplify/core";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/styles";
import { get, size, isEmpty, filter } from "lodash";

import { fetchGet } from "../../../api/fetch";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";

import ProfileSurveyView from "./ProfileSurveyView";
import ProfileSurveyForm from "./ProfileSurveyForm";
import FoldableSection from "../../Common/FoldableSection";

import { getLov } from "../../../utils/lov";

import { LANGUAGES } from "../../../constants/common";

import colors from "../../../assets/style/colors";
import { fetchVerification } from "../../../utils/verification";
import Loader from "../../Common/Loader";

const styles = {
  link: {
    color: colors.primary,
    cursor: "pointer",
    fontWeight: "500",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  section: {
    color: colors.black87,
    fontWeight: 500,
    textTransform: "uppercase",
  },
};

const ProfileSurvey = (props) => {
  const { classes, surveys, profileId, profileName, language } = props;
  const [lov, setLov] = React.useState({});
  const [surveyTypes, setSurveyTypes] = React.useState();
  const [selectedQuestionnaire /*, setSelectedQuestionnaire*/] = React.useState(
    81
  );
  const [audios, setAudios] = React.useState();
  const [editMode, setEditMode] = React.useState(
    get(props, ["onlyEdit", "forceEdition"]) ? true : false
  );
  const [verifications, setVerifications] = React.useState();
  const [verificationFetching, setVerificationsFetching] = React.useState(true);

  const preSurveyId = get(surveys, ["preSurvey", "id"]);
  const lastSurveyId = get(surveys, ["lastSurvey", "id"]);
  const referenceSurveyId = get(surveys, ["referenceSurvey", "id"]);

  React.useEffect(() => {
    if (!isEmpty(surveys)) {
      fetchVerifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(surveys)]);

  const fetchVerifications = async () => {
    try {
      setVerificationsFetching(true);
      const [preSurvey, lastSurvey, referenceSurvey] = await Promise.all([
        fetchVerification(preSurveyId),
        fetchVerification(lastSurveyId),
        fetchVerification(referenceSurveyId),
      ]);
      setVerifications({
        preSurvey,
        lastSurvey,
        referenceSurvey,
      });
      setVerificationsFetching(false);
    } catch (e) {
      console.log(e);
    }
  };

  React.useEffect(() => {
    const fetchAudio = async () => {
      if (surveys) {
        try {
          let res = {};
          let keys = Object.keys(surveys);
          for (let i = 0; i < size(keys); i++) {
            if (get(surveys, [keys[i], "id"])) {
              res[keys[i]] = await fetchGet(
                `survey/audios/${get(surveys, [keys[i], "id"])}`
              );
            }
          }
          setAudios(res);
        } catch (e) {
          console.log(e);
        }
      }
    };
    fetchAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSurveyId, lastSurveyId, referenceSurveyId]);

  React.useEffect(() => {
    const fetchLov = async () => {
      setLov({
        88: await getLov(88),
        eventTypes: await getLov("eventTypes"),
      });
    };
    const fetchSurveyTypes = async () => {
      try {
        let res = await fetchGet("/event_types/81");
        setSurveyTypes([res]);
      } catch (e) {
        console.log(e);
      }
    };
    fetchLov();
    fetchSurveyTypes();
  }, []);

  if (verificationFetching && !editMode) {
    return <Loader noDecoration height="calc(100vh - 420px)" />;
  }

  return (
    <Grid container wrap="nowrap" direction="column">
      {!editMode &&
        !get(surveys, ["preSurvey"]) &&
        !get(surveys, ["lastSurvey"]) &&
        !get(surveys, ["referenceSurvey"]) && (
          <Grid item style={{ marginTop: "30px" }}>
            {I18n.get("site_noQuestionnaireFound")}
          </Grid>
        )}
      {get(surveys, ["preSurvey"]) && (
        <Grid item style={{ marginTop: "30px" }}>
          <Grid container wrap="nowrap">
            <Grid
              item
              xs={props.onlyOneSurvey ? 12 : 6}
              style={{
                paddingRight: get(surveys, ["lastSurvey"]) ? "60px" : "0px",
                maxWidth: "610px",
              }}
            >
              <FoldableSection
                title={get(surveys, ["preSurvey", "type", `name_${language}`])}
                defaultOpen={true}
              >
                <ProfileSurveyView
                  survey={get(surveys, ["preSurvey"])}
                  verification={get(verifications, ["preSurvey"])}
                  language={language}
                  lov={lov}
                  audio={get(audios, ["preSurvey"])}
                  verificationFetching={verificationFetching}
                />
              </FoldableSection>
            </Grid>
            {!props.onlyOneSurvey && <Grid item xs={6} />}
          </Grid>
        </Grid>
      )}
      {(get(surveys, ["referenceSurvey"]) || get(surveys, ["lastSurvey"])) && (
        <Grid item style={{ marginTop: "30px" }}>
          <Grid container>
            {get(surveys, ["referenceSurvey"]) && (
              <Grid
                item
                xs={props.onlyOneSurvey ? 12 : 6}
                style={{
                  paddingRight: get(surveys, ["lastSurvey"]) ? "60px" : "0px",
                  maxWidth: "610px",
                }}
              >
                <Box className={classes.section}>
                  {I18n.get("site_displayedCovidQuestionnaire")}
                </Box>
                <ProfileSurveyView
                  survey={get(surveys, ["referenceSurvey"])}
                  verification={get(verifications, ["referenceSurvey"])}
                  language={language}
                  lov={lov}
                  audio={get(audios, ["referenceSurvey"])}
                />
              </Grid>
            )}
            {get(surveys, ["lastSurvey"]) && (
              <Grid
                item
                xs={props.onlyOneSurvey ? 12 : 6}
                style={{ maxWidth: "610px" }}
              >
                <Box className={classes.section}>
                  {I18n.get("site_newCovidQuestionnaire")}
                </Box>
                <ProfileSurveyView
                  survey={get(surveys, ["lastSurvey"])}
                  verification={
                    get(verifications, ["lastSurvey"]) ||
                    get(surveys, ["lastSurvey", "verification"])
                  }
                  language={language}
                  lov={lov}
                  audio={get(audios, ["lastSurvey"])}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
      {editMode && !isEmpty(lov) && (
        <ProfileSurveyForm
          survey={
            get(props, ["onlyEdit", "selectedQuestionnaire"]) ||
            get(
              filter(surveyTypes, (s) => s.id === selectedQuestionnaire),
              [0]
            )
          }
          profileId={profileId}
          profileName={profileName}
          language={language}
          lov={lov}
          onClose={() => {
            if (get(props, ["onlyEdit", "onClose"])) props.onlyEdit.onClose();
            setEditMode(false);
          }}
          dialog={props.dialog}
          onlyEdit={props.onlyEdit}
          eventGroupId={props.eventGroupId}
        />
      )}
    </Grid>
  );
};

ProfileSurvey.propTypes = {
  surveys: PropTypes.object,
  profileId: PropTypes.string,
  language: PropTypes.oneOf(LANGUAGES),
  eventGroupId: PropTypes.number,
};

export default withStyles(styles)(ProfileSurvey);
